import React, { useState, useRef, useEffect } from 'react';
import {
  Upload, Trash2, Image as ImageIcon, GraduationCap, SlidersHorizontal,
  Type, Palette, Globe, FileText, Landmark, LayoutGrid, AlignJustify,
  Award, Minus, CheckCircle2, QrCode, ListChecks, Download, Eye,
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import useSettingsStore from '../../store/settingsStore';
import { Button, Card, Input, ConfirmDialog, Tabs, Switch, Badge } from '../../components/ui';
import { SUPPORTED_LANGUAGES } from '../../i18n';

// Mirrors backend/src/utils/scoreCardTemplates/index.js's TEMPLATES metadata.
const SCORE_CARD_TEMPLATES = [
  { id: 'classic', name: 'Classic Formal', description: 'Bordered, certificate-style layout with sidebar labels and a verification QR code.', icon: Landmark },
  { id: 'modern', name: 'Modern', description: 'Gradient header, color-coded result signaling, and rounded panels.', icon: LayoutGrid },
  { id: 'compact', name: 'Compact', description: 'A dense, single-column summary for a fast at-a-glance result.', icon: AlignJustify },
  { id: 'elegant', name: 'Elegant Certificate', description: 'Centered, certificate-style composition set in serif type.', icon: Award },
  { id: 'minimal', name: 'Minimal', description: 'Understated black-and-white layout with little color beyond the result.', icon: Minus },
];
const SCORE_CARD_TEMPLATE_NAMES = Object.fromEntries(SCORE_CARD_TEMPLATES.map((t) => [t.id, t.name]));

// Fabricated demo data mirroring the backend's generateSampleScoreCardBuffer,
// so the mini live preview and the real "Download Sample PDF" output agree.
const PREVIEW_DEMO = { name: 'Jordan Rivera', username: 'jordan.rivera', score: 78, total: 100 };

// A small, non-pixel-exact mockup of each PDF template -- not a literal
// render of the PDF (that lives entirely in pdfkit on the backend), but
// enough of each template's real visual identity (gradient header for
// Modern, bordered sidebar for Classic, centered serif for Elegant, etc.)
// using the institution's real accent color/logo/name to be recognizable.
const ScoreCardPreviewMock = ({ templateId, accent, portalName, logoUrl, showQR, showGradeScale }) => {
  const pct = Math.round((PREVIEW_DEMO.score / PREVIEW_DEMO.total) * 100);

  const Logo = ({ size = 'w-7 h-7' }) => (
    <div className={[size, 'rounded flex items-center justify-center overflow-hidden flex-shrink-0'].join(' ')} style={{ backgroundColor: accent }}>
      {logoUrl ? <img src={logoUrl} alt="" className="w-full h-full object-contain" /> : <GraduationCap className="w-4 h-4 text-white" />}
    </div>
  );

  const QRBlock = () => !showQR ? null : (
    <div
      className="w-8 h-8 rounded-sm bg-white border border-slate-300 flex-shrink-0"
      style={{ backgroundImage: 'repeating-linear-gradient(45deg, #1e293b 0 2px, transparent 2px 4px)', backgroundSize: '6px 6px' }}
    />
  );

  const GradeScaleLine = () => !showGradeScale ? null : (
    <p className="text-[6px] text-slate-400 mt-1 leading-tight">Grade scale: A 90%+ · B 80–89% · C 70–79% · D 60–69% · F &lt;60%</p>
  );

  if (templateId === 'modern') {
    return (
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden text-[8px] shadow-sm">
        <div className="p-2.5 flex items-center justify-between" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}aa)` }}>
          <div className="flex items-center gap-1.5 min-w-0">
            <Logo />
            <span className="text-white font-bold text-[9px] truncate">{portalName}</span>
          </div>
          <span className="bg-white rounded-full px-2 py-0.5 text-[7px] font-bold flex-shrink-0" style={{ color: accent }}>PASS</span>
        </div>
        <div className="p-2.5 space-y-2">
          <div className="flex gap-2">
            <div className="flex-1 bg-slate-50 rounded p-1.5 border border-slate-200 border-l-2" style={{ borderLeftColor: '#166534' }}>
              <p className="text-slate-400 text-[6px]">SCORE</p>
              <p className="font-bold text-[12px] leading-tight">{PREVIEW_DEMO.score}/{PREVIEW_DEMO.total}</p>
              <div className="h-1 bg-slate-200 rounded-full mt-1"><div className="h-1 rounded-full bg-green-600" style={{ width: `${pct}%` }} /></div>
            </div>
            <div className="flex-1 bg-white rounded p-1.5 border border-slate-200">
              <p className="text-slate-400 text-[6px]">CANDIDATE</p>
              <p className="font-medium text-[8px] truncate">{PREVIEW_DEMO.name}</p>
              <p className="text-slate-400 text-[6px] mt-1 truncate">{PREVIEW_DEMO.username}</p>
            </div>
          </div>
          <div className="flex items-center justify-between border-t border-slate-100 pt-1.5">
            <QRBlock />
            <p className="text-[6px] text-slate-400">ID: SAMPLE-0000</p>
          </div>
          <GradeScaleLine />
        </div>
      </div>
    );
  }

  if (templateId === 'elegant') {
    return (
      <div className="bg-white rounded-lg border-2 p-3 text-center text-[8px] shadow-sm" style={{ borderColor: accent }}>
        <div className="flex justify-center mb-1"><Logo /></div>
        <p className="font-serif font-bold text-[10px] truncate" style={{ color: accent }}>{portalName}</p>
        <p className="text-slate-400 text-[6px] tracking-wide">EXAMINATION SCORE CARD</p>
        <div className="h-px bg-slate-200 my-1.5" />
        <p className="text-slate-500 text-[7px]">This certifies a result for</p>
        <p className="font-serif font-bold text-[9px] mt-0.5">{PREVIEW_DEMO.name}</p>
        <p className="font-serif font-bold text-[16px] mt-1">{PREVIEW_DEMO.score}/{PREVIEW_DEMO.total}</p>
        <p className="text-green-700 font-bold text-[7px] mt-0.5">PASS</p>
        <div className="flex items-center justify-center gap-2 mt-1.5"><QRBlock /></div>
        <GradeScaleLine />
      </div>
    );
  }

  if (templateId === 'compact') {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-3 text-[8px] shadow-sm">
        <div className="flex items-center gap-1.5 pb-1.5 border-b-2" style={{ borderColor: accent }}>
          <Logo size="w-6 h-6" />
          <span className="font-bold text-[9px] truncate">{portalName}</span>
        </div>
        <p className="text-slate-400 text-[6px] mt-1.5 tracking-wide">SCORE CARD</p>
        <p className="font-bold text-[14px] leading-tight">{PREVIEW_DEMO.score} / {PREVIEW_DEMO.total}</p>
        <p className="text-green-700 font-bold text-[7px]">PASS</p>
        <div className="mt-1.5 space-y-0.5 text-[7px] text-slate-600">
          <div className="flex justify-between"><span className="text-slate-400">Candidate</span><span className="truncate ml-2">{PREVIEW_DEMO.name}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Username</span><span className="truncate ml-2">{PREVIEW_DEMO.username}</span></div>
        </div>
        <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-slate-100">
          <p className="text-[6px] text-slate-400">Scan to verify</p>
          <QRBlock />
        </div>
        <GradeScaleLine />
      </div>
    );
  }

  if (templateId === 'minimal') {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-3 text-[8px] shadow-sm">
        <p className="text-slate-400 text-[6px] tracking-wide truncate">{(portalName || '').toUpperCase()}</p>
        <div className="h-0.5 my-1" style={{ backgroundColor: accent }} />
        <p className="font-bold text-[10px] mt-1">Examination Score Card</p>
        <p className="text-slate-500 text-[7px] mt-1.5">Candidate</p>
        <p className="font-bold text-[8px] truncate">{PREVIEW_DEMO.name}</p>
        <p className="font-bold text-[14px] mt-1.5 leading-tight">{PREVIEW_DEMO.score} / {PREVIEW_DEMO.total}</p>
        <p className="font-bold text-[7px]" style={{ color: '#166534' }}>PASS</p>
        <div className="flex justify-end mt-1.5"><QRBlock /></div>
        <GradeScaleLine />
      </div>
    );
  }

  // classic (default)
  return (
    <div className="bg-white rounded-lg border-2 p-2.5 text-[8px] shadow-sm" style={{ borderColor: accent }}>
      <div className="flex items-center gap-1.5 pb-1.5 min-w-0">
        <Logo />
        <div className="min-w-0">
          <p className="text-slate-400 text-[6px] tracking-wide">EXAMINATION SCORE CARD</p>
          <p className="font-serif font-bold text-[10px] truncate" style={{ color: accent }}>{portalName}</p>
        </div>
      </div>
      <div className="h-px" style={{ backgroundColor: accent }} />
      <div className="flex gap-1.5 mt-1.5 items-stretch">
        <div className="w-2 rounded" style={{ backgroundColor: accent }} />
        <div className="flex-1 grid grid-cols-2 gap-1 min-w-0">
          <div className="border border-slate-200 rounded p-1 min-w-0"><p className="text-slate-400 text-[5px]">NAME</p><p className="text-[7px] font-medium truncate">{PREVIEW_DEMO.name}</p></div>
          <div className="border border-slate-200 rounded p-1 min-w-0"><p className="text-slate-400 text-[5px]">USERNAME</p><p className="text-[7px] font-medium truncate">{PREVIEW_DEMO.username}</p></div>
        </div>
        <QRBlock />
      </div>
      <div className="flex gap-1.5 mt-1.5">
        <div className="w-2 rounded" style={{ backgroundColor: accent }} />
        <div className="flex-1 border border-slate-200 rounded p-1.5 text-center">
          <p className="font-serif font-bold text-[14px] leading-tight">{PREVIEW_DEMO.score}/{PREVIEW_DEMO.total}</p>
          <p className="text-green-700 font-bold text-[7px]">PASS</p>
        </div>
      </div>
      <GradeScaleLine />
    </div>
  );
};

const SETTINGS_TABS = [
  { key: 'branding', label: 'Branding', icon: Palette },
  { key: 'language', label: 'Language', icon: Globe },
  { key: 'scorecard', label: 'Score Card', icon: FileText },
];

// Small card-title row with an icon -- used by every section below so the
// page reads as a set of distinct, scannable settings rather than identical
// gray headings stacked one after another.
const SectionHeading = ({ icon: Icon, title, description }) => (
  <div className="mb-4">
    <div className="flex items-center gap-2 mb-1">
      <span className="w-7 h-7 rounded-lg bg-themePrimary/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-themePrimary" />
      </span>
      <h3 className="text-base font-semibold text-slate-800">{title}</h3>
    </div>
    {description && <p className="text-sm text-slate-500">{description}</p>}
  </div>
);

const Settings = () => {
  const {
    portalName: currentName, logoUrl: currentLogo, loginBgUrl: currentBg,
    primaryColor: currentPrimary, secondaryColor: currentSecondary,
    defaultLanguage: currentDefaultLanguage, scoreCardTemplate: currentScoreCardTemplate,
    scoreCardShowQR: currentShowQR, scoreCardShowGradeScale: currentShowGradeScale,
    setSettings, patchSettings,
  } = useSettingsStore();
  const [portalName, setPortalName] = useState(currentName);
  const [logoUrl, setLogoUrl] = useState(currentLogo);
  const [loginBgUrl, setLoginBgUrl] = useState(currentBg);
  const [primaryColor, setPrimaryColor] = useState(currentPrimary);
  const [secondaryColor, setSecondaryColor] = useState(currentSecondary);
  const [defaultLanguage, setDefaultLanguage] = useState(currentDefaultLanguage);
  // Score card template/toggles are drafted locally and only committed to the
  // backend via the explicit "Save Selection" button, so the admin can try
  // combinations (and download a sample PDF) before anything is persisted.
  const [draftTemplate, setDraftTemplate] = useState(currentScoreCardTemplate);
  const [draftShowQR, setDraftShowQR] = useState(currentShowQR);
  const [draftShowGradeScale, setDraftShowGradeScale] = useState(currentShowGradeScale);
  const [saving, setSaving] = useState(false);
  const [savingColors, setSavingColors] = useState(false);
  const [savingLanguage, setSavingLanguage] = useState(false);
  const [savingSelection, setSavingSelection] = useState(false);
  const [downloadingSample, setDownloadingSample] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'resetColors' | 'removeLogo' | 'removeBg'
  const [activeTab, setActiveTab] = useState('branding');
  const logoInputRef = useRef(null);
  const bgInputRef = useRef(null);

  useEffect(() => {
    setPortalName(currentName);
    setLogoUrl(currentLogo);
    setLoginBgUrl(currentBg);
    setPrimaryColor(currentPrimary);
    setSecondaryColor(currentSecondary);
    setDefaultLanguage(currentDefaultLanguage);
    setDraftTemplate(currentScoreCardTemplate);
    setDraftShowQR(currentShowQR);
    setDraftShowGradeScale(currentShowGradeScale);
  }, [currentName, currentLogo, currentBg, currentPrimary, currentSecondary, currentDefaultLanguage, currentScoreCardTemplate, currentShowQR, currentShowGradeScale]);

  const hasUnsavedScoreCardChanges =
    draftTemplate !== currentScoreCardTemplate || draftShowQR !== currentShowQR || draftShowGradeScale !== currentShowGradeScale;

  const handleSaveScoreCardSelection = async () => {
    setSavingSelection(true);
    try {
      await api.put('/settings', {
        scoreCardTemplate: draftTemplate,
        scoreCardShowQR: draftShowQR,
        scoreCardShowGradeScale: draftShowGradeScale,
      });
      patchSettings({
        scoreCardTemplate: draftTemplate,
        scoreCardShowQR: draftShowQR,
        scoreCardShowGradeScale: draftShowGradeScale,
      });
      toast.success('Score card selection saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSavingSelection(false);
    }
  };

  const handleDownloadSample = async () => {
    setDownloadingSample(true);
    try {
      const res = await api.get('/settings/scorecard-sample', {
        params: { template: draftTemplate, showQR: draftShowQR, showGradeScale: draftShowGradeScale },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Score_Card_Sample_${draftTemplate}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to generate sample PDF');
    } finally {
      setDownloadingSample(false);
    }
  };

  const handleSaveLanguage = async (e) => {
    e.preventDefault();
    setSavingLanguage(true);
    try {
      await api.put('/settings', { defaultLanguage });
      patchSettings({ defaultLanguage });
      toast.success('Default language updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSavingLanguage(false);
    }
  };

  const handleSaveName = async (e) => {
    e.preventDefault();
    if (!portalName.trim()) { toast.error('Portal name cannot be empty'); return; }
    setSaving(true);
    try {
      const res = await api.put('/settings', { portalName: portalName.trim() });
      const updated = res.data.data;
      setSettings(updated.portalName, updated.logoUrl ?? logoUrl, loginBgUrl);
      toast.success('Portal name updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveColors = async (e) => {
    e.preventDefault();
    setSavingColors(true);
    try {
      const res = await api.put('/settings', { primaryColor, secondaryColor });
      const updated = res.data.data;
      setSettings(currentName, currentLogo, currentBg, updated.primaryColor, updated.secondaryColor);
      toast.success('Colors updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save colors');
    } finally {
      setSavingColors(false);
    }
  };

  const handleResetColors = () => {
    setPrimaryColor('#4f46e5');
    setSecondaryColor('#0f172a');
    setConfirmAction(null);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Image must be under 2 MB'); return; }
    const fd = new FormData();
    fd.append('logo', file);
    setUploading(true);
    try {
      const res = await api.post('/settings/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const newLogoUrl = res.data.data.logoUrl;
      setLogoUrl(newLogoUrl);
      setSettings(portalName, newLogoUrl, loginBgUrl);
      toast.success('Logo updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to upload logo');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await api.delete('/settings/logo');
      setLogoUrl('');
      setSettings(portalName, '', loginBgUrl);
      toast.success('Logo removed');
    } catch {
      toast.error('Failed to remove logo');
    } finally {
      setConfirmAction(null);
    }
  };

  const handleBgUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5 MB'); return; }
    const fd = new FormData();
    fd.append('loginBg', file);
    setUploadingBg(true);
    try {
      const res = await api.post('/settings/login-bg', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const newBgUrl = res.data.data.loginBgUrl;
      setLoginBgUrl(newBgUrl);
      setSettings(portalName, logoUrl, newBgUrl);
      toast.success('Login background updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to upload background');
    } finally {
      setUploadingBg(false);
      e.target.value = '';
    }
  };

  const handleRemoveBg = async () => {
    try {
      await api.delete('/settings/login-bg');
      setLoginBgUrl('');
      setSettings(portalName, logoUrl, '');
      toast.success('Login background removed');
    } catch {
      toast.error('Failed to remove background');
    } finally {
      setConfirmAction(null);
    }
  };

  const CONFIRM_CONFIG = {
    resetColors: { title: 'Reset colors?', description: 'This resets to the default Indigo/Slate theme.', onConfirm: handleResetColors, confirmLabel: 'Reset' },
    removeLogo: { title: 'Remove the current logo?', description: 'The default brand mark will be shown instead.', onConfirm: handleRemoveLogo, confirmLabel: 'Remove' },
    removeBg: { title: 'Remove the login background image?', description: 'The login page will fall back to the default dark pattern.', onConfirm: handleRemoveBg, confirmLabel: 'Remove' },
  };

  return (
    <div className="max-w-5xl space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <SlidersHorizontal className="w-6 h-6 text-themePrimary" />
          Portal Settings
        </h2>
        <p className="text-slate-500 text-sm mt-1">Customize branding, the default language, and the score card document for this portal.</p>
      </div>

      <Tabs tabs={SETTINGS_TABS} active={activeTab} onChange={setActiveTab} />

      {activeTab === 'branding' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
          {/* Row 1 */}
          <Card>
            <SectionHeading icon={Type} title="General & Colors" description="This name appears in the sidebar, login page, browser tab, and all headers." />
            <form onSubmit={handleSaveName} className="flex gap-3 items-start">
              <div className="flex-1">
                <Input value={portalName} onChange={e => setPortalName(e.target.value)} placeholder="e.g. Primosa Exams" maxLength={60} />
              </div>
              <Button type="submit" variant="primary" loading={saving} disabled={portalName.trim() === currentName}>
                Save
              </Button>
            </form>
            <p className="text-xs text-slate-400 mt-2">Current: <span className="font-medium text-slate-600">{currentName}</span></p>
          </Card>

          <Card>
            <SectionHeading icon={ImageIcon} title="Portal Logo" description="Upload a logo image (PNG, JPG, SVG — max 2 MB)." />
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-themePrimary flex items-center justify-center flex-shrink-0 overflow-hidden shadow">
                {logoUrl ? (
                  <img src={logoUrl} alt="Portal logo" className="w-full h-full object-contain" />
                ) : (
                  <GraduationCap className="w-8 h-8 text-white" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                <Button size="sm" variant="secondary" icon={Upload} loading={uploading} onClick={() => logoInputRef.current?.click()}>
                  {logoUrl ? 'Change Logo' : 'Upload Logo'}
                </Button>
                {logoUrl && (
                  <Button size="sm" variant="ghost" icon={Trash2} className="!text-red-600 hover:!bg-red-50 !border !border-red-200" onClick={() => setConfirmAction('removeLogo')}>
                    Remove Logo
                  </Button>
                )}
              </div>
            </div>
          </Card>

          {/* Row 2 */}
          <Card>
            <SectionHeading icon={Palette} title="Theme Colors" description="Customize the primary and secondary colors used across the application." />
            <form onSubmit={handleSaveColors} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Primary Color</label>
                  <div className="flex items-center gap-3">
                    <input type="color" value={primaryColor || '#4f46e5'} onChange={e => setPrimaryColor(e.target.value)} className="w-10 h-10 rounded cursor-pointer border-0 p-0 shadow-sm" />
                    <input
                      type="text"
                      value={primaryColor || '#4f46e5'}
                      onChange={e => setPrimaryColor(e.target.value)}
                      className="w-28 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-themePrimary/30 focus:border-themePrimary uppercase"
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Used for buttons and login background gradient.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Secondary Color</label>
                  <div className="flex items-center gap-3">
                    <input type="color" value={secondaryColor || '#0f172a'} onChange={e => setSecondaryColor(e.target.value)} className="w-10 h-10 rounded cursor-pointer border-0 p-0 shadow-sm" />
                    <input
                      type="text"
                      value={secondaryColor || '#0f172a'}
                      onChange={e => setSecondaryColor(e.target.value)}
                      className="w-28 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-themePrimary/30 focus:border-themePrimary uppercase"
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Used for the sidebar and login card center pattern.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button type="submit" variant="primary" loading={savingColors} disabled={primaryColor === currentPrimary && secondaryColor === currentSecondary}>
                  Save Colors
                </Button>
                <Button type="button" variant="outline" onClick={() => setConfirmAction('resetColors')}>
                  Reset to Defaults
                </Button>
              </div>
            </form>
          </Card>

          <Card>
            <SectionHeading icon={ImageIcon} title="Login Page Background" description="Upload a background image for the login page (PNG, JPG — max 5 MB). A dark overlay is applied automatically." />
            <div className="flex items-center gap-4">
              <div className="w-20 h-14 rounded-xl bg-slate-200 flex items-center justify-center flex-shrink-0 overflow-hidden shadow border border-slate-300">
                {loginBgUrl ? (
                  <img src={loginBgUrl} alt="Login background" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-5 h-5 text-slate-400" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input ref={bgInputRef} type="file" accept="image/*" className="hidden" onChange={handleBgUpload} />
                <Button size="sm" variant="secondary" icon={Upload} loading={uploadingBg} onClick={() => bgInputRef.current?.click()}>
                  {loginBgUrl ? 'Change Background' : 'Upload Background'}
                </Button>
                {loginBgUrl && (
                  <Button size="sm" variant="ghost" icon={Trash2} className="!text-red-600 hover:!bg-red-50 !border !border-red-200" onClick={() => setConfirmAction('removeBg')}>
                    Remove Background
                  </Button>
                )}
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-2">Recommended: landscape image, 1920×1080 or larger.</p>
          </Card>
        </div>
      )}

      {activeTab === 'language' && (
        <div className="max-w-2xl">
          <Card>
            <SectionHeading
              icon={Globe}
              title="Default Language"
              description="The language admin, proctor, and student portals start in. Anyone who has already picked their own language (via the language switcher) keeps their choice."
            />
            <form onSubmit={handleSaveLanguage} className="flex gap-3 items-start">
              <select
                value={defaultLanguage}
                onChange={e => setDefaultLanguage(e.target.value)}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-themePrimary/30 focus:border-themePrimary"
              >
                {SUPPORTED_LANGUAGES.map(({ code, label }) => (
                  <option key={code} value={code}>{label}</option>
                ))}
              </select>
              <Button type="submit" variant="primary" loading={savingLanguage} disabled={defaultLanguage === currentDefaultLanguage}>
                Save
              </Button>
            </form>
          </Card>
        </div>
      )}

      {activeTab === 'scorecard' && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 items-start">
          <div className="space-y-6">
            <Card>
              <SectionHeading
                icon={FileText}
                title="Score Card Template"
                description="Choose the layout used for every score card PDF download — by students, and by admins/proctors from an exam's Results tab."
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {SCORE_CARD_TEMPLATES.map((tpl) => {
                  const selected = draftTemplate === tpl.id;
                  const Icon = tpl.icon;
                  return (
                    <button
                      key={tpl.id}
                      type="button"
                      onClick={() => setDraftTemplate(tpl.id)}
                      className={[
                        'relative text-left rounded-xl border-2 p-4 transition',
                        selected ? 'border-themePrimary bg-themePrimary/5 shadow-sm' : 'border-slate-200 hover:border-slate-300 hover:shadow-sm',
                      ].join(' ')}
                    >
                      {selected && (
                        <CheckCircle2 className="w-5 h-5 text-themePrimary absolute top-3 right-3" />
                      )}
                      <span className={[
                        'w-9 h-9 rounded-lg flex items-center justify-center mb-3',
                        selected ? 'bg-themePrimary text-white' : 'bg-slate-100 text-slate-500',
                      ].join(' ')}>
                        <Icon className="w-5 h-5" />
                      </span>
                      <p className="font-semibold text-slate-800 pr-6">{tpl.name}</p>
                      <p className="text-xs text-slate-500 mt-1">{tpl.description}</p>
                    </button>
                  );
                })}
              </div>
            </Card>

            <Card>
              <SectionHeading icon={SlidersHorizontal} title="Template Options" description="Extra content every template can optionally include." />
              <div className="divide-y divide-slate-100">
                <Switch
                  checked={draftShowQR}
                  onChange={setDraftShowQR}
                  label={<span className="flex items-center gap-1.5"><QrCode className="w-3.5 h-3.5 text-slate-400" /> Include QR Verification Code</span>}
                  description="Enables instant authentication via QR scan. Disable to show only a text verification ID."
                />
                <Switch
                  checked={draftShowGradeScale}
                  onChange={setDraftShowGradeScale}
                  label={<span className="flex items-center gap-1.5"><ListChecks className="w-3.5 h-3.5 text-slate-400" /> Show Grade Scale</span>}
                  description="Displays a percentage-to-grade legend (A/B/C/D/F) in the notes section."
                />
              </div>
            </Card>
          </div>

          {/* Live preview panel */}
          <Card className="lg:sticky lg:top-6">
            <div className="flex items-center gap-2 mb-1">
              <Eye className="w-4 h-4 text-themePrimary" />
              <h3 className="text-sm font-semibold text-slate-800">Live Template Preview</h3>
              <Badge variant="brand" className="ml-auto">{SCORE_CARD_TEMPLATE_NAMES[draftTemplate]}</Badge>
            </div>
            <p className="text-xs text-slate-500 mb-4">Interactive preview of {currentName || 'Portal'} Score Card (A4).</p>

            <div className="bg-slate-100 rounded-xl p-5 flex justify-center">
              <div className="w-full max-w-[220px]">
                <ScoreCardPreviewMock
                  templateId={draftTemplate}
                  accent={currentPrimary}
                  portalName={currentName}
                  logoUrl={currentLogo}
                  showQR={draftShowQR}
                  showGradeScale={draftShowGradeScale}
                />
              </div>
            </div>

            <div className="flex flex-col gap-2 mt-4">
              <Button variant="secondary" icon={Download} loading={downloadingSample} onClick={handleDownloadSample}>
                Download Sample PDF
              </Button>
              <Button variant="primary" icon={CheckCircle2} loading={savingSelection} disabled={!hasUnsavedScoreCardChanges} onClick={handleSaveScoreCardSelection}>
                Save Selection
              </Button>
            </div>
          </Card>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={confirmAction ? CONFIRM_CONFIG[confirmAction].onConfirm : undefined}
        title={confirmAction ? CONFIRM_CONFIG[confirmAction].title : ''}
        description={confirmAction ? CONFIRM_CONFIG[confirmAction].description : ''}
        confirmLabel={confirmAction ? CONFIRM_CONFIG[confirmAction].confirmLabel : 'Confirm'}
        variant={confirmAction === 'resetColors' ? 'default' : 'danger'}
      />
    </div>
  );
};

export default Settings;
